import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Link } from 'react-router';
import { ApiError } from '../lib/api';
import { money, titleCase } from '../lib/format';
import { dismiss, useToasts } from '../lib/toast';

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

// --- Buttons ------------------------------------------------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'ai';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-strong shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(1_64_46/0.3)] disabled:bg-brand/50',
  secondary: 'bg-white/80 text-ink border border-line-strong/90 shadow-[0_1px_2px_rgb(27_26_24/0.05)] hover:bg-white hover:border-faint/70 disabled:text-faint',
  ghost: 'text-ink-2 hover:bg-ink/[0.05] disabled:text-faint',
  danger: 'bg-white/80 text-bad border border-line-strong/90 hover:bg-bad-soft hover:border-bad/40 disabled:text-faint',
  ai: 'bg-ai text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.18),0_1px_2px_rgb(70_2_47/0.3)] hover:brightness-110 disabled:opacity-50',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  loading,
  icon,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md'; loading?: boolean; icon?: ReactNode }) {
  // Clicks on buttons count in the owner's usage analytics (lib/analytics.ts) under their text, so a button's text is the
  // app's own wording, never data from someone's books: pass data-track="…" to name it otherwise.
  return (
    <button
      type="button"
      data-track=""
      {...rest}
      disabled={rest.disabled || loading}
      className={cx(
        'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-[10px] font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed',
        size === 'sm' ? 'h-7 px-2.5 text-[12.5px]' : 'h-8 px-3 text-[13px]',
        VARIANTS[variant],
        className,
      )}
    >
      {loading ? <Spinner className="size-3.5" /> : icon}
      {children}
    </button>
  );
}

export function ButtonLink({ to, variant = 'secondary', size = 'md', icon, children, className }: { to: string; variant?: Variant; size?: 'sm' | 'md'; icon?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <Link
      to={to}
      data-track=""
      className={cx(
        'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-[10px] font-medium whitespace-nowrap transition-colors',
        size === 'sm' ? 'h-7 px-2.5 text-[12.5px]' : 'h-8 px-3 text-[13px]',
        VARIANTS[variant],
        className,
      )}
    >
      {icon}
      {children}
    </Link>
  );
}

export function Spinner({ className = 'size-4' }: { className?: string }) {
  return (
    <svg className={cx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity=".2" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// --- Surfaces -----------------------------------------------------------------------------------------------------

export function Card({ title, subtitle, actions, children, className, pad = true, footer }: { title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children?: ReactNode; className?: string; pad?: boolean; footer?: ReactNode }) {
  return (
    <section className={cx('glass rounded-2xl', className)}>
      {(title || actions) && (
        <header className="flex min-h-12 items-center justify-between gap-3 border-b border-ink/[0.06] px-4 py-2">
          <div className="min-w-0">
            {title && <h2 className="app-title truncate text-[14.5px] text-ink">{title}</h2>}
            {subtitle && <p className="text-[12px] leading-snug text-muted">{subtitle}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={pad ? 'p-4' : cx('overflow-hidden', title || actions ? (footer ? '' : 'rounded-b-2xl') : footer ? 'rounded-t-2xl' : 'rounded-2xl')}>{children}</div>
      {footer && <footer className="border-t border-ink/[0.06] px-4 py-2.5">{footer}</footer>}
    </section>
  );
}

export function PageHeader({ title, description, actions, children, eyebrow }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; children?: ReactNode; eyebrow?: ReactNode }) {
  return (
    <div className="mb-5">
      {eyebrow && <div className="app-eyebrow mb-2 text-muted">{eyebrow}</div>}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="app-title text-[27px] leading-[1.1] text-ink">{title}</h1>
          {description && <p className="mt-1 max-w-3xl text-[13.5px] text-ink-2/85">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

// --- Badges -------------------------------------------------------------------------------------------------------

export type Tone = 'neutral' | 'good' | 'warn' | 'bad' | 'info' | 'ai' | 'brand';
const TONES: Record<Tone, string> = {
  neutral: 'bg-sunken text-ink-2 ring-line-strong/60',
  good: 'bg-good-soft text-good ring-good/20',
  warn: 'bg-warn-soft text-warn ring-warn/25',
  bad: 'bg-bad-soft text-bad ring-bad/20',
  info: 'bg-info-soft text-info ring-info/20',
  ai: 'bg-ai-soft text-ai ring-ai/20',
  brand: 'bg-brand-soft text-brand ring-brand/20',
};

export function Badge({ tone = 'neutral', children, dot, className }: { tone?: Tone; children: ReactNode; dot?: boolean; className?: string }) {
  return (
    <span className={cx('inline-flex h-5 items-center gap-1 rounded-full px-2 text-[11.5px] font-medium whitespace-nowrap ring-1 ring-inset', TONES[tone], className)}>
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

const STATUS_TONE: Record<string, Tone> = {
  POSTED: 'good',
  PAID: 'good',
  MATCHED: 'good',
  ACTIVE: 'good',
  APPROVED: 'good',
  DELIVERED: 'good',
  PASS: 'good',
  DONE: 'good',
  CLOSED: 'neutral',
  COMPLETED: 'neutral',
  ISSUED: 'info',
  OPEN: 'info',
  PARTIALLY_PAID: 'warn',
  PENDING_APPROVAL: 'warn',
  PENDING: 'warn',
  IN_PROGRESS: 'warn',
  WARN: 'warn',
  UNMATCHED: 'warn',
  PLANNED: 'neutral',
  DRAFT: 'neutral',
  TODO: 'neutral',
  EXCLUDED: 'neutral',
  REVERSED: 'neutral',
  VOID: 'neutral',
  TERMINATED: 'neutral',
  CANCELLED: 'neutral',
  REJECTED: 'bad',
  FAILED: 'bad',
  FAIL: 'bad',
  OVERDUE: 'bad',
};

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  return (
    <Badge tone={STATUS_TONE[status] ?? 'neutral'} dot>
      {label ?? titleCase(status)}
    </Badge>
  );
}

const SOURCE_TONE: Record<string, Tone> = { AI: 'ai', MCP: 'ai', API: 'info', IMPORT: 'neutral', SYSTEM: 'brand', UI: 'neutral' };
const SOURCE_LABEL: Record<string, string> = { AI: 'AI', MCP: 'MCP', API: 'API', IMPORT: 'Import', SYSTEM: 'System', UI: 'Manual' };

export function SourceBadge({ source }: { source: string }) {
  return <Badge tone={SOURCE_TONE[source] ?? 'neutral'}>{SOURCE_LABEL[source] ?? source}</Badge>;
}

// --- Data ---------------------------------------------------------------------------------------------------------

/** Money cell: tabular, right-aligned by the parent, negatives in the danger colour when asked. */
export function Money({ v, c = 'USD', compact, colored, sign, className }: { v: number | null | undefined; c?: string; compact?: boolean; colored?: boolean; sign?: boolean; className?: string }) {
  return <span className={cx('num whitespace-nowrap', colored && v !== null && v !== undefined && v < 0 && 'text-bad', colored && v !== null && v !== undefined && v > 0 && sign && 'text-good', className)}>{money(v, c, { compact, sign })}</span>;
}

export function Table({ children, className, dense }: { children: ReactNode; className?: string; dense?: boolean }) {
  return (
    <div className={cx('overflow-x-auto bg-white/50 scrollbar-thin', className)}>
      <table className={cx('w-full border-collapse text-left', dense ? '[&_td]:py-1.5 [&_th]:py-1.5' : '[&_td]:py-2.5 [&_th]:py-2')}>{children}</table>
    </div>
  );
}

export function Th({ children, right, className, w }: { children?: ReactNode; right?: boolean; className?: string; w?: string }) {
  return (
    <th style={w ? { width: w } : undefined} className={cx('border-b border-ink/[0.08] px-3 text-[11.5px] font-medium tracking-wide text-muted uppercase first:pl-4 last:pr-4', right && 'text-right', className)}>
      {children}
    </th>
  );
}

export function Td({ children, right, className, mono, muted, colSpan }: { children?: ReactNode; right?: boolean; className?: string; mono?: boolean; muted?: boolean; colSpan?: number }) {
  return (
    <td colSpan={colSpan} className={cx('border-b border-ink/[0.06] px-3 align-middle first:pl-4 last:pr-4', right && 'num text-right', mono && 'font-mono text-[12px]', muted && 'text-muted', className)}>
      {children}
    </td>
  );
}

export function Tr({ children, onClick, className, active }: { children: ReactNode; onClick?: () => void; className?: string; active?: boolean }) {
  return (
    <tr onClick={onClick} className={cx(onClick && 'cursor-pointer hover:bg-brand-soft/40', active && 'bg-brand-soft/50', className)}>
      {children}
    </tr>
  );
}

export function Stat({ label, value, sub, tone, children, className }: { label: ReactNode; value: ReactNode; sub?: ReactNode; tone?: 'good' | 'bad' | 'warn'; children?: ReactNode; className?: string }) {
  return (
    <div className={cx('glass min-w-0 rounded-2xl px-4 py-3.5', className)}>
      <div className="text-[12px] font-medium text-muted">{label}</div>
      <div className="num mt-1 truncate text-[22px] leading-tight font-semibold tracking-[-0.01em] text-ink">{value}</div>
      {sub && <div className={cx('mt-0.5 truncate text-[12px]', tone === 'good' ? 'text-good' : tone === 'bad' ? 'text-bad' : tone === 'warn' ? 'text-warn' : 'text-muted')}>{sub}</div>}
      {children}
    </div>
  );
}

export function KeyValues({ items, cols = 2 }: { items: [ReactNode, ReactNode][]; cols?: 1 | 2 | 3 | 4 }) {
  return (
    <dl className={cx('grid gap-x-6 gap-y-3', cols === 1 ? 'grid-cols-1' : cols === 2 ? 'grid-cols-2' : cols === 3 ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-4')}>
      {items.map(([k, v], i) => (
        <div key={i} className="min-w-0">
          <dt className="text-[11.5px] text-muted">{k}</dt>
          <dd className="mt-0.5 truncate text-[13px] text-ink">{v ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Progress({ value, tone = 'brand', className }: { value: number; tone?: 'brand' | 'good' | 'warn' | 'bad'; className?: string }) {
  const color = tone === 'good' ? 'bg-good' : tone === 'warn' ? 'bg-warn' : tone === 'bad' ? 'bg-bad' : 'bg-brand';
  return (
    <div className={cx('h-1.5 overflow-hidden rounded-full bg-ink/[0.07]', className)}>
      <div className={cx('h-full rounded-full transition-[width]', color)} style={{ width: `${Math.max(0, Math.min(100, value * 100))}%` }} />
    </div>
  );
}

// --- States -------------------------------------------------------------------------------------------------------

export function Loading({ label = 'Loading…', className }: { label?: string; className?: string }) {
  return (
    <div className={cx('flex items-center justify-center gap-2 py-12 text-[13px] text-muted', className)}>
      <Spinner /> {label}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded-lg bg-ink/[0.06]', className)} />;
}

export function Empty({ title, children, icon, action }: { title: ReactNode; children?: ReactNode; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      {icon && <div className="mb-3 text-faint">{icon}</div>}
      <div className="text-[13.5px] font-medium text-ink">{title}</div>
      {children && <div className="mt-1 max-w-md text-[12.5px] text-muted">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorNote({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  const msg = error instanceof ApiError || error instanceof Error ? error.message : String(error);
  return (
    <div role="alert" className={cx('rounded-md border border-bad/25 bg-bad-soft px-3 py-2 text-[12.5px] text-bad', className)}>
      {msg}
    </div>
  );
}

/** Wraps a query: spinner while loading, the error when it fails, the content once data is there. */
export function Query<T>({ q, children, loading }: { q: { data?: T; error: unknown; isLoading: boolean }; children: (data: T) => ReactNode; loading?: ReactNode }) {
  if (q.isLoading) return <>{loading ?? <Loading />}</>;
  if (q.error) return <ErrorNote error={q.error} className="m-4" />;
  if (q.data === undefined) return null;
  return <>{children(q.data)}</>;
}

// --- Tabs ---------------------------------------------------------------------------------------------------------

export function Tabs<K extends string>({ items, value, onChange, className }: { items: { key: K; label: ReactNode; count?: number }[]; value: K; onChange: (k: K) => void; className?: string }) {
  return (
    // Scrolls sideways rather than wrapping labels when space is tight; the underline is an inset shadow so scrolling cannot clip it.
    <div role="tablist" className={cx('flex gap-1 overflow-x-auto border-b border-line [scrollbar-width:none]', className)}>
      {items.map((t) => (
        <button
          key={t.key}
          role="tab"
          aria-selected={value === t.key}
          onClick={() => onChange(t.key)}
          className={cx(
            'flex h-9 shrink-0 items-center gap-1.5 px-2.5 text-[13px] font-medium whitespace-nowrap transition-colors',
            value === t.key ? 'text-ink shadow-[inset_0_-2px_0_var(--color-brand)]' : 'text-muted hover:text-ink',
          )}
        >
          {t.label}
          {t.count !== undefined && <span className={cx('num rounded-full px-1.5 text-[11px]', value === t.key ? 'bg-brand-soft text-brand' : 'bg-sunken text-muted')}>{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Segmented<K extends string>({ items, value, onChange }: { items: { key: K; label: ReactNode }[]; value: K; onChange: (k: K) => void }) {
  return (
    <div className="inline-flex rounded-[10px] border border-ink/[0.07] bg-ink/[0.04] p-0.5">
      {items.map((t) => (
        <button key={t.key} aria-pressed={value === t.key} onClick={() => onChange(t.key)} className={cx('h-6 rounded-[8px] px-2.5 text-[12px] font-medium transition-colors', value === t.key ? 'bg-white text-ink shadow-card' : 'text-muted hover:text-ink')}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// --- Forms --------------------------------------------------------------------------------------------------------

const inputBase =
  'rounded-[9px] border border-line-strong bg-surface px-2.5 text-[13px] text-ink placeholder:text-faint transition-colors hover:border-faint focus:border-brand focus:ring-2 focus:ring-brand/15 focus:outline-none disabled:bg-sunken disabled:text-muted';

/** Controls fill their container unless the caller sizes them; Tailwind cannot let a later `w-40` beat `w-full`. */
const sized = (className?: string, height = 'h-8') => cx(inputBase, height, !/(^|\s)w-/.test(className ?? '') && 'w-full');

export function Field({ label, hint, error, children, className }: { label?: ReactNode; hint?: ReactNode; error?: string; children: ReactNode; className?: string }) {
  return (
    <label className={cx('block min-w-0', className)}>
      {label && <span className="mb-1 block text-[12px] font-medium text-ink-2">{label}</span>}
      {children}
      {error ? <span className="mt-1 block text-[11.5px] text-bad">{error}</span> : hint ? <span className="mt-1 block text-[11.5px] text-muted">{hint}</span> : null}
    </label>
  );
}

export function Input({ className, invalid, ...rest }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return <input {...rest} className={cx(sized(className), invalid && 'border-bad focus:border-bad focus:ring-bad/15', className)} />;
}

export function MoneyInput({ className, currency, ...rest }: InputHTMLAttributes<HTMLInputElement> & { currency?: string }) {
  return (
    <div className="relative">
      <input inputMode="decimal" autoComplete="off" {...rest} className={cx(sized(className), 'num pr-11 text-right', className)} />
      {currency && <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-[11px] text-faint">{currency}</span>}
    </div>
  );
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={cx(sized(className), 'appearance-none bg-[length:16px] bg-[right_6px_center] bg-no-repeat pr-7', className)} style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2375726a'%3E%3Cpath d='M5.5 7.5 10 12l4.5-4.5'/%3E%3C/svg%3E")` }}>
      {children}
    </select>
  );
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={cx(sized(className, /(^|\s)min-h-/.test(className ?? '') ? '' : 'min-h-16'), 'py-1.5 leading-snug', className)} />;
}

export function Toggle({ checked, onChange, label, description, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; description?: ReactNode; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4">
      <label htmlFor={id} className="min-w-0 cursor-pointer">
        <div className="text-[13px] font-medium text-ink">{label}</div>
        {description && <div className="text-[12px] text-muted">{description}</div>}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx('relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-50', checked ? 'bg-brand' : 'bg-line-strong')}
      >
        <span className={cx('absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow transition-transform', checked && 'translate-x-4')} />
      </button>
    </div>
  );
}

// --- Overlays -----------------------------------------------------------------------------------------------------

export type MenuEntry =
  | { label: ReactNode; onSelect: () => void; icon?: ReactNode; danger?: boolean; disabled?: boolean; checked?: boolean }
  | { heading: ReactNode }
  | 'divider';

/**
 * A button that opens a list of actions. Closes on a choice, Escape or a click outside; arrow keys move between
 * items. Opens upward when there is no room below.
 */
export function Menu({ label, items, children, className, button, align = 'right' }: { label: string; items: MenuEntry[]; children: ReactNode; className?: string; button?: string; align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        ref.current?.querySelector<HTMLElement>('button')?.focus();
      }
    };
    document.addEventListener('mousedown', away);
    window.addEventListener('keydown', key);
    list.current?.querySelector<HTMLElement>('[role^=menuitem]:not(:disabled)')?.focus();
    return () => {
      document.removeEventListener('mousedown', away);
      window.removeEventListener('keydown', key);
    };
  }, [open]);
  const toggle = () => {
    const r = ref.current?.getBoundingClientRect();
    setUp(!!r && window.innerHeight - r.bottom < 360 && r.top > window.innerHeight - r.bottom);
    setOpen((o) => !o);
  };
  const arrows = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const all = [...(list.current?.querySelectorAll<HTMLElement>('[role^=menuitem]:not(:disabled)') ?? [])];
    const i = all.indexOf(document.activeElement as HTMLElement);
    all[(i + (e.key === 'ArrowDown' ? 1 : -1) + all.length) % all.length]?.focus();
  };
  return (
    <div ref={ref} className={cx('relative', open && 'z-40', className)}>
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        className={button ?? 'inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-lg px-1.5 text-muted transition-colors hover:bg-ink/[0.06] hover:text-ink aria-expanded:bg-ink/[0.06] aria-expanded:text-ink'}
      >
        {children}
      </button>
      {open && (
        <div
          ref={list}
          role="menu"
          onKeyDown={arrows}
          className={cx('glass-float animate-fade-up absolute z-40 max-h-[min(70vh,520px)] w-56 overflow-y-auto rounded-xl p-1 scrollbar-thin', align === 'right' ? 'right-0' : 'left-0', up ? 'bottom-full mb-1.5' : 'top-full mt-1.5')}
        >
          {items.map((it, i) =>
            it === 'divider' ? (
              <div key={i} className="my-1 border-t border-ink/[0.07]" />
            ) : 'heading' in it ? (
              <div key={i} className="app-eyebrow px-2.5 pt-2 pb-1 text-[10px] text-faint">
                {it.heading}
              </div>
            ) : (
              <button
                key={i}
                type="button"
                role={it.checked === undefined ? 'menuitem' : 'menuitemradio'}
                aria-checked={it.checked}
                disabled={it.disabled}
                onClick={() => {
                  setOpen(false);
                  it.onSelect();
                }}
                className={cx(
                  'flex h-8 w-full items-center gap-2 rounded-lg px-2.5 text-left text-[13px] outline-none disabled:opacity-40',
                  it.danger ? 'text-bad hover:bg-bad-soft focus-visible:bg-bad-soft' : 'text-ink hover:bg-ink/[0.05] focus-visible:bg-ink/[0.05]',
                )}
              >
                {it.checked !== undefined ? <span className={cx('flex size-4 items-center justify-center text-brand', !it.checked && 'invisible')}><CheckIcon /></span> : it.icon && <span className="text-muted">{it.icon}</span>}
                <span className="min-w-0 flex-1 truncate">{it.label}</span>
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg className="size-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4.5 10.5l3.5 3.5 7.5-8" />
    </svg>
  );
}

function useEscape(onClose: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
}

export function Modal({ open, onClose, title, children, footer, width = 'max-w-lg' }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; width?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEscape(onClose);
  useEffect(() => {
    if (open) ref.current?.querySelector<HTMLElement>('input,select,textarea,button')?.focus();
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/25 p-4 pt-[10vh] backdrop-blur-[3px]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} role="dialog" aria-modal className={cx('glass-float animate-fade-up w-full rounded-2xl', width)}>
        <header className="flex items-center justify-between border-b border-ink/[0.07] px-5 py-3">
          <h2 className="app-title text-[16px]">{title}</h2>
          <button onClick={onClose} className="rounded-lg p-1 text-muted hover:bg-ink/[0.05] hover:text-ink" aria-label="Close">
            <XIcon />
          </button>
        </header>
        <div className="px-5 py-4">{children}</div>
        {footer && <footer className="flex justify-end gap-2 rounded-b-2xl border-t border-ink/[0.07] bg-white/40 px-5 py-3">{footer}</footer>}
      </div>
    </div>
  );
}

export function Drawer({ open, onClose, title, subtitle, children, actions, width = 'max-w-2xl' }: { open: boolean; onClose: () => void; title: ReactNode; subtitle?: ReactNode; children: ReactNode; actions?: ReactNode; width?: string }) {
  useEscape(onClose);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-ink/20 p-3 backdrop-blur-[2px]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside role="dialog" aria-modal className={cx('glass-float animate-slide-in flex h-full w-full flex-col rounded-2xl', width)}>
        <header className="flex items-start justify-between gap-3 border-b border-ink/[0.07] px-5 py-3.5">
          <div className="min-w-0">
            <h2 className="app-title truncate text-[16px]">{title}</h2>
            {subtitle && <div className="mt-0.5 text-[12.5px] text-muted">{subtitle}</div>}
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <button onClick={onClose} className="rounded-lg p-1 text-muted hover:bg-ink/[0.05] hover:text-ink" aria-label="Close">
              <XIcon />
            </button>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto rounded-b-2xl scrollbar-thin">{children}</div>
      </aside>
    </div>
  );
}

export function Toaster() {
  const toasts = useToasts();
  return (
    <div className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-80 flex-col gap-2" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={cx('glass-float animate-fade-up pointer-events-auto flex items-start gap-2.5 rounded-xl px-3.5 py-2.5 text-[13px]', t.kind === 'error' && 'outline outline-bad/30')}>
          <span className={cx('mt-1.5 size-2 shrink-0 rounded-full', t.kind === 'error' ? 'bg-bad' : t.kind === 'success' ? 'bg-good' : 'bg-info')} />
          <span className="min-w-0 flex-1 text-ink">
            {t.message}
            {t.action && (
              <a href={t.action.href} className="mt-1 block font-medium text-brand hover:underline">
                {t.action.label} →
              </a>
            )}
          </span>
          <button onClick={() => dismiss(t.id)} className="text-faint hover:text-ink" aria-label="Dismiss">
            <XIcon className="size-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

export function XIcon({ className = 'size-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M5 5l10 10M15 5L5 15" />
    </svg>
  );
}

export function Avatar({ name, className }: { name: string; className?: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
  return <span className={cx('inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-soft text-[11px] font-semibold text-brand', className)}>{initials}</span>;
}

/** A labelled section divider used inside cards and drawers. */
export function SectionTitle({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <h3 className="app-eyebrow text-muted">{children}</h3>
      {actions}
    </div>
  );
}
